import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Card,
  CardContent,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { format, isValid } from 'date-fns';
import { getPublicVolunteerRegistrations, deleteRegistration } from '../services/api';
import { Registration } from '../models/types';
import { useAuth } from '../contexts/AuthContext';

const formatDate = (dateValue: string | Date | null | undefined, formatString: string, fallback: string = 'N/A'): string => {
  if (!dateValue) return fallback;
  
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  return isValid(date) ? format(date, formatString) : fallback;
};

const VolunteerDashboardPage: React.FC = () => {
  const navigate = useNavigate();  const { volunteer, isAuthenticated, loading: authLoading } = useAuth();  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [registrationToDelete, setRegistrationToDelete] = useState<Registration | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetchRegistrations = async () => {
      if (!volunteer?.id || !isAuthenticated || authLoading) {
        return;
      }
      
      try {
        setLoading(true);
        console.log(`Fetching registrations for volunteer ID: ${volunteer.id}`);
        const data = await getPublicVolunteerRegistrations(volunteer.id);
        console.log(`Received ${data.length} registrations`);
        setRegistrations(data);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching registrations:', err);
        if (err.response && err.response.status === 401) {
          console.log('Authentication error when fetching registrations');
        } else {
          setError('Failed to load your registrations. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRegistrations();
  }, [volunteer, isAuthenticated, authLoading]);

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRegistrationToDelete(null);
  };
  const handleDeleteConfirm = async () => {
    if (!registrationToDelete) return;

    try {
      setDeleting(true);
      await deleteRegistration(registrationToDelete.id);
      
      // Remove the deleted registration from the list
      setRegistrations(prev => prev.filter(reg => reg.id !== registrationToDelete.id));
      
      setSuccessMessage(`Anmeldung für "${registrationToDelete.jobName}" wurde erfolgreich gelöscht.`);
      setDeleteDialogOpen(false);
      setRegistrationToDelete(null);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Error deleting registration:', err);
      setError('Fehler beim Löschen der Anmeldung. Bitte versuchen Sie es erneut.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading || authLoading || (!volunteer && isAuthenticated)) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          {authLoading ? 'Überprüfe Anmeldung...' : 'Lade Helfer-Profil...'}
        </Typography>
      </Container>
    );
  }

  if (!volunteer || !isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">
          Sie müssen angemeldet sein, um diese Seite aufzurufen.
        </Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate('/login')}
            sx={{ mx: 1 }}
          >
            Anmelden
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/')}
            sx={{ mx: 1 }}
          >
            Zur Startseite
          </Button>
        </Box>
      </Container>
    );
  }

  // Regular loading state for data fetching
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Lade Ihre Anmeldungen...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Helfer-Dashboard
        </Typography>

        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Ihre Informationen
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flexBasis: { xs: '100%', sm: 'calc(50% - 8px)' }, flexGrow: 0 }}>
              <Typography variant="body1">
                <strong>Name:</strong> {volunteer.name}
              </Typography>
            </Box>
            <Box sx={{ flexBasis: { xs: '100%', sm: 'calc(50% - 8px)' }, flexGrow: 0 }}>
              <Typography variant="body1">
                <strong>Email:</strong> {volunteer.email}
              </Typography>
            </Box>
            <Box sx={{ flexBasis: { xs: '100%', sm: 'calc(50% - 8px)' }, flexGrow: 0 }}>
              <Typography variant="body1">
                <strong>Organization:</strong> {volunteer.commodity || 'Not specified'}
              </Typography>
            </Box>
            <Box sx={{ flexBasis: { xs: '100%', sm: 'calc(50% - 8px)' }, flexGrow: 0 }}>
              <Typography variant="body1">
                <strong>Total Time Volunteered:</strong> {volunteer.totalMinutesWorked || 0} minutes
              </Typography>
            </Box>
           
          </Box>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/jobs')}
            >
              Weitere Dienste durchsuchen
            </Button>
          </Box>
        </Paper>

        <Typography variant="h5" gutterBottom>
          Ihre Anmeldungen
        </Typography>

        {error && <Alert severity="error">{error}</Alert>}
        
        {successMessage && (
          <Alert 
            severity="success" 
            sx={{ mb: 2 }}
            onClose={() => setSuccessMessage(null)}
          >
            {successMessage}
          </Alert>
        )}

        {registrations.length === 0 ? (
          <Alert severity="info">
            Sie haben sich noch für keine Dienste angemeldet. Schauen Sie sich die verfügbaren Dienste an.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {registrations.map((reg) => (
              <Box key={reg.id} sx={{ flexBasis: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)' }, flexGrow: 0 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {reg.jobName || 'Unnamed Job'}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">
                      <strong>Start:</strong> {formatDate(reg.timeslotStart, 'MMM d, h:mm a')}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>End:</strong> {formatDate(reg.timeslotEnd, 'MMM d, h:mm a')}
                    </Typography>
                    
                    <Divider sx={{ my: 1 }} />
                      <Typography variant="body2">
                      <strong>Registration Date:</strong> {formatDate(reg.registeredAt, 'MMM d, yyyy')}
                    </Typography>

                    {reg.comment && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mt: 1, 
                          p: 1, 
                          bgcolor: 'action.hover', 
                          borderRadius: 1,
                          fontStyle: 'italic'
                        }}
                      >
                        <strong>Ihr Kommentar:</strong> "{reg.comment}"
                      </Typography>
                    )}
                      <Box sx={{ mt: 2 }}>
                      {reg.isConfirmed ? (
                        <Chip label="Confirmed" color="success" size="small" />
                      ) : (
                        <Chip label="Pending Confirmation" color="warning" size="small" />
                      )}
                      
                      {reg.hasAttended && (
                        <Chip label="Attended" color="primary" size="small" sx={{ ml: 1 }} />
                      )}
                      
                      {reg.actualMinutesWorked && (
                        <Chip 
                          label={`Worked: ${reg.actualMinutesWorked} min`} 
                          color="secondary" 
                          size="small" 
                          sx={{ mt: 1 }} 
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Anmeldung löschen
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Sind Sie sicher, dass Sie Ihre Anmeldung für "{registrationToDelete?.jobName}" löschen möchten?
              {registrationToDelete && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="body2">
                    <strong>Dienst:</strong> {registrationToDelete.jobName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Start:</strong> {formatDate(registrationToDelete.timeslotStart, 'EEEE, dd.MM.yyyy HH:mm')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Ende:</strong> {formatDate(registrationToDelete.timeslotEnd, 'HH:mm')}
                  </Typography>
                  {registrationToDelete.comment && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      <strong>Ihr Kommentar:</strong> "{registrationToDelete.comment}"
                    </Typography>
                  )}
                </Box>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleDeleteCancel}
              disabled={deleting}
            >
              Abbrechen
            </Button>
            <Button 
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            >
              {deleting ? 'Lösche...' : 'Löschen'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default VolunteerDashboardPage;

